import { get } from 'lodash';
import React, { FormEvent, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Img } from 'react-image';
import { useMutation } from 'react-query';
import { isPhoneOrEmail } from 'shared-utils';
import { sendOtp } from 'src/apis/user';
import DriveAttendanceUserPhone from 'src/components/OfflineDriveUserAttendance/DriveAttendanceUserPhone';
import DrivePhoneVerification from 'src/components/OfflineDriveUserAttendance/DrivePhoneVerification';
import OfflineDriveUserRegistration from 'src/components/OfflineDriveUserAttendance/OfflineDriveUserRegistration';
import { Drawer } from 'src/components/shared/Drawer';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPhoneVerificationStatus } from 'src/hooks/user.hooks';
import { OfflineDocumentsTypes } from 'types';

interface VerificationPayload {
	emailOrPhone: string;
	driveId?: string;
	documents: OfflineDocumentsTypes[];
}

export const OfflineDriveAttendancePage = () => {
	const { user } = useUserContext();
	const { themeMode } = useThemeContext();
	const [showSendOTPScreen, setShowSendOTPScreen] = useState(true);
	const [showVerifyOTPScreen, setShowVerifyOTPScreen] = useState(false);
	const [driveUserPhone, setDriveUserPhone] = useState<string>('');
	const [driveUserOTP, setDriveUserOTP] = useState(['', '', '', '', '', '']);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [otpSubmitted, setOtpSubmitted] = useState<boolean>(false);
	const [changeOtpMethod, setChangeOtpMethod] = useState(false);
	const [isUserNotRegistered, setIsUserNotRegistered] = useState(false);
	const [isOpenOtpDrawer, setIsOpenOtpDrawer] = useState<boolean>(false);
	const [isLoadingVerifyOtp, setIsLoadingVerifyOtp] = useState<boolean>(false);
	const [selectedDrive, setSelectedDrive] = useState<{ label: string; value: string } | undefined>({
		label: '',
		value: '',
	});
	const [selectedLocation, setSelectedLocation] = useState<{ label: string; value: string } | null>(null);
	const [documents, setDocuments] = useState<OfflineDocumentsTypes[]>([]);

	const phoneVerificationStatus = useGetPhoneVerificationStatus(driveUserPhone);

	const [formRegisteredDriveData, setFormRegisteredDriveData] = useState<any>({});
	const toast = useToast();
	const generateVerificationCodeFn = useMutation({
		mutationKey: 'sendOtp',
		mutationFn: sendOtp,
		onSuccess() {
			const type = isPhoneOrEmail(driveUserPhone);
			toast({
				type: 'success',
				title: `An OTP has sent to your registered ${type}.`,
			});
			setIsLoading(false);
			setOtpSubmitted(true);
			setIsOpenOtpDrawer(true);
			phoneVerificationStatus.refetch();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			if (errorMessage === 'User not registered') {
				setIsUserNotRegistered(true);
			} else {
				toast({
					type: 'error',
					title: errorMessage,
				});
			}
			setIsLoading(false);
		},
	});
	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		// Use selected location driveId if available, otherwise use selected drive
		const finalDriveId = selectedLocation?.value || selectedDrive?.value || '';
		const verificationPayload: VerificationPayload = {
			emailOrPhone: driveUserPhone.trim().toLowerCase(),
			driveId: finalDriveId,
			documents,
		};

		const phoneRegex = /^[0-9]{10}$/;

		if (!phoneRegex.test(driveUserPhone.trim().toLowerCase())) {
			toast({
				type: 'error',
				title: `Phone number is not valid!`,
			});
			return;
		}

		setIsLoading(true);
		setChangeOtpMethod(false);
		console.log('verificationPayload:', verificationPayload);
		generateVerificationCodeFn.mutate(verificationPayload);
	};

	return (
		<>
			<div className="flex items-center justify-center cursor-pointer">
				{user ? null : (
					<Img
						src={
							themeMode === 'dark'
								? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
								: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
						}
						width="110"
						height="32px"
						alt="LevelUp Logo"
						className="mt-4"
					/>
				)}
			</div>
			{isUserNotRegistered ? (
				<>
					<OfflineDriveUserRegistration
						prefilledPhone={driveUserPhone}
						prefilledDrive={selectedDrive ?? { label: '', value: '' }}
						setIsOpenOtpDrawer={setIsOpenOtpDrawer}
						isOpenOtpDrawer={isOpenOtpDrawer}
						setFormRegisteredDriveData={setFormRegisteredDriveData}
						isLoadingVerifyOtp={isLoadingVerifyOtp}
						setIsLoadingVerifyOtp={setIsLoadingVerifyOtp}
					/>
					{isLoadingVerifyOtp && (
						<Drawer
							maxHeight="90dvh"
							minHeight="max-content"
							boxShadow="0px -8px 32px 0px #00000040"
							isOpen={isOpenOtpDrawer}
							isModal={!isMobile}
							onClose={() => {
								setIsOpenOtpDrawer(false);
							}}
							header={<></>}
							body={
								<DrivePhoneVerification
									driveUserPhone={driveUserPhone}
									setDriveUserPhone={setDriveUserPhone}
									setOtpSubmitted={setOtpSubmitted}
									btnText={'Submit'}
									changeOtpMethod={changeOtpMethod}
									setChangeOtpMethod={setChangeOtpMethod}
									setIsOpenOtpDrawer={setIsOpenOtpDrawer}
									formRegisteredDriveData={formRegisteredDriveData}
									documents={documents}
									selectedLocation={selectedLocation}
								/>
							}
						/>
					)}
				</>
			) : (
				<>
					<DriveAttendanceUserPhone
						driveUserPhone={driveUserPhone}
						setDriveUserPhone={setDriveUserPhone}
						isLoading={isLoading}
						setSelectedDrive={setSelectedDrive}
						selectedDrive={selectedDrive ?? { label: '', value: '' }}
						handleSubmit={handleSubmit}
						documents={documents}
						setDocuments={setDocuments}
						selectedLocation={selectedLocation}
						setSelectedLocation={setSelectedLocation}
					/>
					<Drawer
						maxHeight="90dvh"
						minHeight="max-content"
						boxShadow="0px -8px 32px 0px #00000040"
						isOpen={isOpenOtpDrawer}
						isModal={!isMobile}
						onClose={() => {
							setIsOpenOtpDrawer(false);
						}}
						header={<></>}
						body={
							<DrivePhoneVerification
								driveUserPhone={driveUserPhone}
								setDriveUserPhone={setDriveUserPhone}
								setOtpSubmitted={setOtpSubmitted}
								btnText={'Submit'}
								changeOtpMethod={changeOtpMethod}
								setChangeOtpMethod={setChangeOtpMethod}
								setIsOpenOtpDrawer={setIsOpenOtpDrawer}
								setSelectedDrive={setSelectedDrive}
								selectedDrive={selectedDrive ?? { label: '', value: '' }}
								documents={documents}
								selectedLocation={selectedLocation}
							/>
						}
					/>
				</>
			)}
		</>
	);
};
